// next/shellFlag.ts — which shell App renders: the voice-first shell behind a flag, today's shells by default.
//
//   ?ui=next              → NextShell for this page load (nothing is stored)
//   ?ui=classic|off|0     → today's shells, and the stored preference is cleared
//   localStorage va-ui-next=1 → NextShell (the persisted preference; the avatar menu sets and clears it)
//   neither               → today's shells (MobileShell / DesktopShell), exactly as before
//
// The decision is a PURE function (SE ui-next-shell SE-01 pins it, with the guard that App.tsx keeps its default
// branch verbatim); the two thin readers below are the only code that touches the URL and localStorage, each wrapped
// in try/catch (storage can be absent or throw in private windows and previews).

export type UiShell = 'next' | 'classic';
export type ThemeVariant = 'dark' | 'light';

export const UI_NEXT_STORAGE_KEY = 'va-ui-next';
export const UI_NEXT_THEME_KEY = 'va-ui-next-theme';

const ON = new Set(['next', '1', 'on', 'true']);
const OFF = new Set(['classic', '0', 'off', 'false']);

export interface ShellDecision {
  shell: UiShell;
  /** What to do with the stored preference: 'clear' removes it, null leaves it untouched. */
  storage: 'clear' | null;
}

/** Pure: the query string and the stored value in, the shell and the storage action out. */
export function resolveUiShell(input: { search: string; stored: string | null }): ShellDecision {
  const raw = new URLSearchParams(input.search).get('ui')?.trim().toLowerCase() ?? null;
  if (raw !== null && ON.has(raw)) return { shell: 'next', storage: null };
  if (raw !== null && OFF.has(raw)) return { shell: 'classic', storage: 'clear' };
  return { shell: input.stored === '1' ? 'next' : 'classic', storage: null };
}

/** Pure: the theme variant for the voice-first shell (`?uiTheme=` wins for the load, then the stored choice, else dark). */
export function resolveNextTheme(input: { search: string; stored: string | null }): ThemeVariant {
  const q = new URLSearchParams(input.search).get('uiTheme')?.trim().toLowerCase();
  if (q === 'light' || q === 'dark') return q;
  return input.stored === 'light' ? 'light' : 'dark';
}

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** True when this page load renders the voice-first shell. Applies the 'clear' storage action as a side effect. */
export function isNextShellEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  const d = resolveUiShell({ search: window.location.search, stored: readStored(UI_NEXT_STORAGE_KEY) });
  if (d.storage === 'clear') {
    try {
      localStorage.removeItem(UI_NEXT_STORAGE_KEY);
    } catch {
      /* storage unavailable: the URL decided this load anyway */
    }
  }
  return d.shell === 'next';
}

/** The persisted preference (avatar menu): on keeps the new shell across loads, off returns to today's. */
export function setNextShellPreference(on: boolean): void {
  try {
    if (on) localStorage.setItem(UI_NEXT_STORAGE_KEY, '1');
    else localStorage.removeItem(UI_NEXT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function readNextTheme(): ThemeVariant {
  if (typeof window === 'undefined') return 'dark';
  return resolveNextTheme({ search: window.location.search, stored: readStored(UI_NEXT_THEME_KEY) });
}

export function writeNextTheme(v: ThemeVariant): void {
  try {
    localStorage.setItem(UI_NEXT_THEME_KEY, v);
  } catch {
    /* ignore */
  }
}
